import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { Button } from './ui/button';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Code,
  Undo,
  Redo,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Textarea } from './ui/textarea';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Start typing...',
  className,
  minHeight = '120px',
}: RichTextEditorProps) {
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [htmlSource, setHtmlSource] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['paragraph'],
        alignments: ['left', 'center', 'right'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer nofollow',
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  const addLink = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const switchToHtml = () => {
    if (editor) {
      setHtmlSource(editor.getHTML());
      setIsHtmlMode(true);
    }
  };

  const switchToRich = () => {
    if (editor) {
      editor.commands.setContent(htmlSource);
      onChange(htmlSource);
    }
    setIsHtmlMode(false);
  };

  if (!editor) return null;

  return (
    <div className={cn('border rounded-md overflow-hidden bg-background', className)}>
      <div className="border-b px-2 py-1.5 flex items-center gap-1 flex-wrap bg-muted/30">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-7 w-7 p-0', editor.isActive('bold') && 'bg-muted')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={isHtmlMode}
        >
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-7 w-7 p-0', editor.isActive('italic') && 'bg-muted')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={isHtmlMode}
        >
          <Italic className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-7 w-7 p-0', editor.isActive('underline') && 'bg-muted')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={isHtmlMode}
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
        </Button>

        <div className="h-4 w-px bg-border mx-0.5" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-7 w-7 p-0', editor.isActive({ textAlign: 'left' }) && 'bg-muted')}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          disabled={isHtmlMode}
        >
          <AlignLeft className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-7 w-7 p-0', editor.isActive({ textAlign: 'center' }) && 'bg-muted')}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          disabled={isHtmlMode}
        >
          <AlignCenter className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-7 w-7 p-0', editor.isActive({ textAlign: 'right' }) && 'bg-muted')}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          disabled={isHtmlMode}
        >
          <AlignRight className="h-3.5 w-3.5" />
        </Button>

        <div className="h-4 w-px bg-border mx-0.5" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-7 w-7 p-0', editor.isActive('bulletList') && 'bg-muted')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={isHtmlMode}
        >
          <List className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-7 w-7 p-0', editor.isActive('orderedList') && 'bg-muted')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={isHtmlMode}
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-7 w-7 p-0', editor.isActive('link') && 'bg-muted')}
          onClick={addLink}
          disabled={isHtmlMode}
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </Button>

        <div className="h-4 w-px bg-border mx-0.5" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={isHtmlMode}
        >
          <Undo className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={isHtmlMode}
        >
          <Redo className="h-3.5 w-3.5" />
        </Button>

        <div className="ml-auto">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn('h-7 px-2 text-xs', isHtmlMode && 'bg-muted')}
            onClick={isHtmlMode ? switchToRich : switchToHtml}
          >
            <Code className="h-3.5 w-3.5 mr-1" />
            HTML
          </Button>
        </div>
      </div>

      {isHtmlMode ? (
        <Textarea
          value={htmlSource}
          onChange={(e) => setHtmlSource(e.target.value)}
          className="border-0 rounded-none focus-visible:ring-0 font-mono text-xs resize-none"
          style={{ minHeight }}
        />
      ) : (
        <EditorContent
          editor={editor}
          className="prose prose-sm dark:prose-invert max-w-none px-3 py-2 focus-within:outline-none [&_.tiptap]:outline-none [&_.tiptap]:min-h-[var(--editor-min-h)]"
          style={{ '--editor-min-h': minHeight } as React.CSSProperties}
        />
      )}
    </div>
  );
}
